import { useCallback, useEffect, useRef, useState } from 'react';
import { competitionAPI, expeditionAPI, tradeAPI } from '../api';

/**
 * useShellCounters
 *
 * The three badge counts the app shell shows in the sidebar: expeditions
 * waiting to be collected, competition rewards to claim, and incoming trade
 * proposals. WebSocket events keep them live; these fetches are the fallback
 * for what the socket can't tell us (a count from before we connected, and
 * expeditions that simply run out of time with no server-side event).
 *
 * @param {boolean} isAuthenticated counts are zeroed and fetching stops when false
 */

/** How long a set of counts is considered fresh enough to skip a refetch. */
const STALE_AFTER_MS = 30000;

/** Expeditions complete on a timer, so poll that one count on its own. */
const EXPEDITION_POLL_MS = 60000;

export function useShellCounters(isAuthenticated) {
    const [expeditionsToCollect, setExpeditionsToCollect] = useState(0);
    const [competitionRewardsToClaim, setCompetitionRewardsToClaim] = useState(0);
    const [tradeProposalsPending, setTradeProposalsPending] = useState(0);
    const lastRefreshRef = useRef(0);

    // Every fetch below fails silently: a missing badge is not worth an error
    // in the user's face.
    const refreshExpeditions = useCallback(async () => {
        try {
            const response = await expeditionAPI.getCounts();
            setExpeditionsToCollect(response.data?.ready_to_collect || 0);
        } catch (err) {
            // Ignore - the badge just keeps its previous value.
        }
    }, []);

    const refreshCompetitionRewards = useCallback(async () => {
        try {
            const response = await competitionAPI.getMyEntries();
            const entries = response.data || [];
            setCompetitionRewardsToClaim(
                entries.filter(entry => entry.rewards && !entry.rewards_claimed).length
            );
        } catch (err) {
            // Ignore - the badge just keeps its previous value.
        }
    }, []);

    const refreshTradeProposals = useCallback(async () => {
        try {
            const response = await tradeAPI.getPendingProposalsCount();
            setTradeProposalsPending(response.data?.count || 0);
        } catch (err) {
            // Ignore - the badge just keeps its previous value.
        }
    }, []);

    /** Refresh all three counts. */
    const refreshCounts = useCallback(() => {
        lastRefreshRef.current = Date.now();
        return Promise.all([
            refreshExpeditions(),
            refreshCompetitionRewards(),
            refreshTradeProposals(),
        ]);
    }, [refreshExpeditions, refreshCompetitionRewards, refreshTradeProposals]);

    /**
     * Refresh only if the counts have had time to drift. The WebSocket calls
     * this on every (re)connect, which on a flaky link can be often; signing in
     * already fetched them a moment earlier.
     */
    const refreshCountsIfStale = useCallback(() => {
        if (Date.now() - lastRefreshRef.current < STALE_AFTER_MS) return;
        refreshCounts();
    }, [refreshCounts]);

    // Live nudges from the WebSocket, which reports the event but not the total.
    const addCompetitionReward = useCallback(() => {
        setCompetitionRewardsToClaim(count => count + 1);
    }, []);

    const addTradeProposal = useCallback(() => {
        setTradeProposalsPending(count => count + 1);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            setExpeditionsToCollect(0);
            setCompetitionRewardsToClaim(0);
            setTradeProposalsPending(0);
            lastRefreshRef.current = 0;
            return undefined;
        }

        refreshCounts();
        const interval = setInterval(refreshExpeditions, EXPEDITION_POLL_MS);
        return () => clearInterval(interval);
    }, [isAuthenticated, refreshCounts, refreshExpeditions]);

    return {
        expeditionsToCollect,
        competitionRewardsToClaim,
        tradeProposalsPending,
        refreshCounts,
        refreshCountsIfStale,
        refreshExpeditions,
        refreshCompetitionRewards,
        refreshTradeProposals,
        addCompetitionReward,
        addTradeProposal,
    };
}

export default useShellCounters;
